import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  bindRecycleOrderReception,
  listRecycleOrderReception,
  unbindRecycleOrderReception,
} from 'apis/oms';
import LOCALS from 'commons/locals';
import usePagination from 'commons/hooks/usePagination';
import { parseAsInteger, parseAsString, useQueryStates } from 'nuqs';
import i18n from '../../../i18n';
import { RecycleOrderReception } from 'types/oms';
import formatTimeInTimezone from 'utils/formatTimeInTimezone';

const JAPAN_TIMEZONE = 'Asia/Tokyo';

type BoundFilter = '' | 'bound' | 'unbound';

const toBoundFilter = (value: string): BoundFilter =>
  value === 'bound' || value === 'unbound' ? value : '';

const ReceptionList = () => {
  const { loading, setLoading, dataSource, setDataSource, total, setTotal } =
    usePagination<RecycleOrderReception>();
  const [query, setQuery] = useQueryStates({
    bound: parseAsString.withDefault(''),
    name: parseAsString.withDefault(''),
    tel: parseAsString.withDefault(''),
    submissionId: parseAsString.withDefault(''),
    pageNum: parseAsInteger.withDefault(1),
    pageSize: parseAsInteger.withDefault(10),
  });
  const [bound, setBound] = useState<BoundFilter>(() =>
    toBoundFilter(query.bound),
  );
  const [name, setName] = useState(query.name);
  const [tel, setTel] = useState(query.tel);
  const [submissionId, setSubmissionId] = useState(query.submissionId);
  const [bindTarget, setBindTarget] = useState<RecycleOrderReception | null>(
    null,
  );
  const [bindValue, setBindValue] = useState('');
  const [binding, setBinding] = useState(false);
  const [unbindingId, setUnbindingId] = useState<number | null>(null);

  useEffect(() => {
    setBound(toBoundFilter(query.bound));
    setName(query.name);
    setTel(query.tel);
    setSubmissionId(query.submissionId);
  }, [query.bound, query.name, query.submissionId, query.tel]);

  const load = useCallback(() => {
    const parsedSubmissionId = Number(query.submissionId);
    const boundFilter = toBoundFilter(query.bound);
    setLoading(true);
    listRecycleOrderReception({
      pageNum: query.pageNum,
      pageSize: query.pageSize,
      bound: boundFilter || undefined,
      name: query.name.trim() || undefined,
      tel: query.tel.trim() || undefined,
      submissionId:
        query.submissionId.trim() && Number.isInteger(parsedSubmissionId)
          ? parsedSubmissionId
          : undefined,
    })
      .then((res) => {
        setDataSource(res.list || []);
        setTotal(res.total || 0);
      })
      .finally(() => setLoading(false));
  }, [
    query.bound,
    query.name,
    query.pageNum,
    query.pageSize,
    query.submissionId,
    query.tel,
    setDataSource,
    setLoading,
    setTotal,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  const search = () => {
    const nextBound = bound;
    const nextName = name.trim();
    const nextTel = tel.trim();
    const nextSubmissionId = submissionId.trim();
    const unchanged =
      nextBound === toBoundFilter(query.bound) &&
      nextName === query.name &&
      nextTel === query.tel &&
      nextSubmissionId === query.submissionId &&
      query.pageNum === 1;
    setQuery({
      bound: nextBound,
      name: nextName,
      tel: nextTel,
      submissionId: nextSubmissionId,
      pageNum: 1,
    });
    if (unchanged) load();
  };

  const reset = () => {
    setBound('');
    setName('');
    setTel('');
    setSubmissionId('');
    const unchanged =
      toBoundFilter(query.bound) === '' &&
      query.name === '' &&
      query.tel === '' &&
      query.submissionId === '' &&
      query.pageNum === 1 &&
      query.pageSize === 10;
    setQuery({
      bound: '',
      name: '',
      tel: '',
      submissionId: '',
      pageNum: 1,
      pageSize: 10,
    });
    if (unchanged) load();
  };

  const confirmBind = async () => {
    if (!bindTarget) return;
    const parsed = Number(bindValue);
    if (!Number.isInteger(parsed) || parsed <= 0) {
      message.warning(i18n.t(LOCALS.reception_submission_invalid));
      return;
    }
    setBinding(true);
    try {
      await bindRecycleOrderReception({
        id: bindTarget.id,
        submissionId: parsed,
      });
      message.success(i18n.t(LOCALS.reception_bind_success));
      setBindTarget(null);
      setBindValue('');
      load();
    } finally {
      setBinding(false);
    }
  };

  const confirmUnbind = async (record: RecycleOrderReception) => {
    setUnbindingId(record.id);
    try {
      await unbindRecycleOrderReception(record.id);
      message.success(i18n.t(LOCALS.reception_unbind_success));
      load();
    } finally {
      setUnbindingId(null);
    }
  };

  const columns: ColumnsType<RecycleOrderReception> = [
    {
      title: i18n.t(LOCALS.reception_date),
      dataIndex: 'createTime',
      render: (value: string) => formatTimeInTimezone(value, JAPAN_TIMEZONE),
    },
    {
      title: i18n.t(LOCALS.name),
      dataIndex: 'name',
      render: (value: string) => value || '-',
    },
    {
      title: i18n.t(LOCALS.phone),
      dataIndex: 'tel',
      render: (value: string) => value || '-',
    },
    {
      title: i18n.t(LOCALS.sHXnOEVSrC),
      dataIndex: 'submissionId',
      render: (value: number | null) =>
        value == null ? (
          '-'
        ) : (
          <Link
            to={`/rrs/recycling-consignment-list?submissionId=${value}&status=all`}
          >
            <Button type="link" className="p-0">
              {value}
            </Button>
          </Link>
        ),
    },
    {
      title: i18n.t(LOCALS.operation),
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Link to={`/rrs/reception/${record.id}?lang=ja`}>
            <Button type="link">{i18n.t(LOCALS.view)}</Button>
          </Link>
          {record.submissionId == null ? (
            <Button
              type="link"
              onClick={() => {
                setBindTarget(record);
                setBindValue('');
              }}
            >
              {i18n.t(LOCALS.reception_bind)}
            </Button>
          ) : (
            <Popconfirm
              title={i18n.t(LOCALS.reception_unbind_confirm)}
              okButtonProps={{ loading: unbindingId === record.id }}
              onConfirm={() => confirmUnbind(record)}
            >
              <Button type="link" loading={unbindingId === record.id}>
                {i18n.t(LOCALS.reception_unbind)}
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="p-4">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Select
          className="w-36"
          value={bound}
          onChange={setBound}
          options={[
            { value: '', label: i18n.t(LOCALS.all) },
            { value: 'bound', label: i18n.t(LOCALS.reception_bound) },
            { value: 'unbound', label: i18n.t(LOCALS.reception_unbound) },
          ]}
        />
        <Input
          className="w-44"
          allowClear
          placeholder={i18n.t(LOCALS.name) ?? undefined}
          value={name}
          onChange={(event) => setName(event.target.value)}
          onPressEnter={search}
        />
        <Input
          className="w-44"
          allowClear
          placeholder={i18n.t(LOCALS.phone) ?? undefined}
          value={tel}
          onChange={(event) => setTel(event.target.value)}
          onPressEnter={search}
        />
        <Input
          className="w-44"
          allowClear
          placeholder={i18n.t(LOCALS.sHXnOEVSrC) ?? undefined}
          value={submissionId}
          onChange={(event) => setSubmissionId(event.target.value)}
          onPressEnter={search}
        />
        <Button type="primary" onClick={search}>
          {i18n.t(LOCALS.search)}
        </Button>
        <Button onClick={reset}>{i18n.t(LOCALS.reset)}</Button>
        <Link to="/rrs/reception/new?lang=ja" className="ml-auto">
          <Button type="primary">{i18n.t(LOCALS.new_reception_form)}</Button>
        </Link>
      </div>
      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={dataSource}
        pagination={{
          current: query.pageNum,
          pageSize: query.pageSize,
          total,
          showSizeChanger: true,
          onChange: (nextPage, nextPageSize) => {
            setQuery({ pageNum: nextPage, pageSize: nextPageSize });
          },
        }}
      />
      <Modal
        title={i18n.t(LOCALS.reception_bind)}
        open={Boolean(bindTarget)}
        confirmLoading={binding}
        onOk={confirmBind}
        onCancel={() => setBindTarget(null)}
      >
        <Input
          placeholder={i18n.t(LOCALS.sHXnOEVSrC) ?? undefined}
          value={bindValue}
          onChange={(event) => setBindValue(event.target.value)}
        />
      </Modal>
    </div>
  );
};

export default ReceptionList;
